import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { getContactInfo, submitContactMessage } from '../services/api';
import { Mail, Phone, Send, Instagram, Twitter, Linkedin } from 'lucide-react';
import { toast } from 'sonner';

const ContactFormContent = ({ contactInfo }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Submit form
      await submitContactMessage(formData);
      toast.success('Message sent successfully! I will get back to you soon.');
      setFormData({ name: '', email: '', message: '' });
    } catch (err) {
      console.error('Error submitting message:', err);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Define social links with icons
  const socialLinks = [
    { key: 'instagram', url: contactInfo?.social?.instagram, icon: <Instagram className="text-white" size={24} />, label: 'Instagram' },
    { key: 'twitter', url: contactInfo?.social?.twitter, icon: <Twitter className="text-white" size={24} />, label: 'Twitter/X' },
    { key: 'linkedin', url: contactInfo?.social?.linkedin, icon: <Linkedin className="text-white" size={24} />, label: 'LinkedIn' },
  ].filter(link => link.url); // Only show links with valid URLs

  return (
    <>
      <main className="pt-28 md:pt-40 pb-16 px-8">
        <div className="max-w-[1000px] mx-auto">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.25, 0.4, 0.25, 1] }}
              className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-purple-600 font-['Poppins'] text-6xl font-black mb-6 pb-2"
            >
              GET IN TOUCH
            </motion.h2>
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.6, delay: 0.2, ease: [0.25, 0.4, 0.25, 1] }}
              className="h-1 w-32 bg-gradient-to-r from-purple-500 to-pink-500 mx-auto rounded-full"
            />
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3, ease: [0.25, 0.4, 0.25, 1] }}
              className="text-gray-400 mt-6 text-lg"
            >
              Let's collaborate on your next project
            </motion.p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Contact Form - Shows first on mobile, second on desktop */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5, ease: [0.25, 0.4, 0.25, 1] }}
              className="glass-effect rounded-2xl p-8 md:order-2"
            >
              <h3 className="text-white font-['Poppins'] text-2xl font-bold mb-6">
                SEND MESSAGE
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your Name"
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition-all"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Your Email"
                    required
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition-all"
                  />
                </div>
                <div>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Your Message"
                    required
                    rows="4"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition-all resize-none"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-purple-500/30"
                >
                  {submitting ? (
                    'SENDING...'
                  ) : (
                    <>
                      <Send size={18} />
                      SEND MESSAGE
                    </>
                  )}
                </button>
              </form>
            </motion.div>

            {/* Contact Info - Shows second on mobile, first on desktop */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6, ease: [0.25, 0.4, 0.25, 1] }}
              className="glass-effect rounded-2xl p-8 md:order-1"
            >
              <h3 className="text-white font-['Poppins'] text-2xl font-bold mb-6">
                CONTACT INFO
              </h3>
              <div className="space-y-6">
                {contactInfo?.email && (
                  <div className="flex items-center gap-4 group">
                    <div className="p-3 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500">
                      <Mail className="text-white" size={24} />
                    </div>
                    <a
                      href={`mailto:${contactInfo.email}`}
                      className="text-gray-300 hover:text-white transition-colors text-lg"
                    >
                      <span className="hidden sm:inline">{contactInfo.email}</span>
                      <span className="sm:hidden">Email</span>
                    </a>
                  </div>
                )}
                {contactInfo?.phone && (
                  <div className="flex items-center gap-4 group">
                    <div className="p-3 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500">
                      <Phone className="text-white" size={24} />
                    </div>
                    <a
                      href={`tel:${contactInfo.phone}`}
                      className="text-gray-300 hover:text-white transition-colors text-lg"
                    >
                      {contactInfo.phone}
                    </a>
                  </div>
                )}

                {/* Divider */}
                {socialLinks.length > 0 && (
                  <div className="flex items-center gap-3 my-8">
                    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-purple-500/30 to-transparent"></div>
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-1.5 rounded-full bg-purple-500/50"></div>
                      <div className="h-1.5 w-1.5 rounded-full bg-pink-500/50"></div>
                      <div className="h-1.5 w-1.5 rounded-full bg-purple-500/50"></div>
                    </div>
                    <div className="flex-1 h-px bg-gradient-to-r from-transparent via-purple-500/30 to-transparent"></div>
                  </div>
                )}

                {/* Social Links */}
                {socialLinks.length > 0 && (
                  <div className="space-y-6">
                    {socialLinks.map(({ key, url, icon, label }) => (
                      <div key={key} className="flex items-center gap-4 group">
                        <div className="p-3 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500">
                          {icon}
                        </div>
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-300 hover:text-white transition-colors text-lg"
                        >
                          {label}
                        </a>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </main>
    </>
  );
};

const Contact = () => {
  const [contactInfo, setContactInfo] = useState(null);

  useEffect(() => {
    fetchContactInfo();
  }, []);

  const fetchContactInfo = async () => {
    try {
      const data = await getContactInfo();
      setContactInfo(data);
    } catch (err) {
      console.error('Error fetching contact info:', err);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#1a1a2e] to-[#0a0a0f]">
      <SEO
        title="Contact Charles Kilvington | Video Editing & Motion Graphics Services"
        description="Get in touch with Charles Kilvington for video editing, motion graphics design, animation, and cinematography services. Available for freelance and contract work."
        keywords="hire video editor, motion graphics services, video production contact, freelance video editor, Las Vegas video editor"
        ogTitle="Contact Charles Kilvington | Video Editing & Motion Graphics Services"
        ogDescription="Get in touch for video editing, motion graphics design, animation, and cinematography services."
        ogUrl="https://charleskilvington.com/contact"
        ogImage="https://charleskilvington.com/og-image.jpg"
        twitterTitle="Contact Charles Kilvington"
        twitterDescription="Get in touch for video editing, motion graphics, and cinematography services."
        twitterImage="https://charleskilvington.com/og-image.jpg"
      />
      <Header />
      <ContactFormContent contactInfo={contactInfo} />
      <Footer />
    </div>
  );
};

export default Contact;
